import type { ApiError, ErrorCode } from '@omni/shared';
import { zh } from '../i18n';

/**
 * The one place that talks HTTP (CONTRACTS §2, §3).
 *
 * CSRF: every non-GET `/api` request carries `X-Omni-CSRF` equal to the
 * `omni_csrf` cookie (which is deliberately NOT HttpOnly so this can read it)
 * and a same-origin `Origin` the browser supplies. `credentials: 'same-origin'`
 * sends `omni_sid`.
 */

export const API_BASE = '/api';

export class ApiRequestError extends Error {
  readonly code: ErrorCode | 'NETWORK';
  readonly status: number;
  readonly details: Record<string, unknown> | undefined;

  constructor(
    code: ErrorCode | 'NETWORK',
    message: string,
    status: number,
    details?: Record<string, unknown>,
  ) {
    super(message);
    this.name = 'ApiRequestError';
    this.code = code;
    this.status = status;
    this.details = details;
  }

  get retryAfterSec(): number | null {
    const value = this.details?.['retryAfterSec'];
    return typeof value === 'number' ? value : null;
  }
}

export function isApiError(value: unknown, code?: ErrorCode | 'NETWORK'): value is ApiRequestError {
  if (!(value instanceof ApiRequestError)) return false;
  return code === undefined || value.code === code;
}

function readCookie(name: string): string | null {
  const prefix = `${name}=`;
  for (const part of document.cookie.split(';')) {
    const trimmed = part.trim();
    if (trimmed.startsWith(prefix)) return decodeURIComponent(trimmed.slice(prefix.length));
  }
  return null;
}

/** Fired on any 401 so the router can bounce to /login without prop-drilling. */
export const UNAUTHENTICATED_EVENT = 'omni:unauthenticated';

function isApiErrorBody(value: unknown): value is ApiError {
  if (typeof value !== 'object' || value === null) return false;
  const error = (value as { error?: unknown }).error;
  if (typeof error !== 'object' || error === null) return false;
  const { code, message } = error as { code?: unknown; message?: unknown };
  return typeof code === 'string' && typeof message === 'string';
}

export interface RequestOptions {
  readonly method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  readonly body?: unknown;
  readonly signal?: AbortSignal;
  readonly query?: Record<string, string | number | boolean | undefined | null>;
}

export function buildUrl(path: string, query?: RequestOptions['query']): string {
  const url = `${API_BASE}${path}`;
  if (!query) return url;
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === '') continue;
    params.set(key, String(value));
  }
  const qs = params.toString();
  return qs ? `${url}?${qs}` : url;
}

async function parseBody(response: Response): Promise<unknown> {
  const type = response.headers.get('content-type') ?? '';
  if (!type.includes('application/json')) return await response.text();
  try {
    return await response.json();
  } catch {
    return null;
  }
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const method = options.method ?? 'GET';
  const headers: Record<string, string> = { Accept: 'application/json' };
  if (options.body !== undefined) headers['Content-Type'] = 'application/json';
  if (method !== 'GET') {
    const csrf = readCookie('omni_csrf');
    if (csrf) headers['X-Omni-CSRF'] = csrf;
  }

  let response: Response;
  try {
    response = await fetch(buildUrl(path, options.query), {
      method,
      headers,
      credentials: 'same-origin',
      ...(options.body === undefined ? {} : { body: JSON.stringify(options.body) }),
      ...(options.signal ? { signal: options.signal } : {}),
    });
  } catch (cause) {
    throw new ApiRequestError('NETWORK', zh.connection.offline, 0, {
      cause: cause instanceof Error ? cause.message : String(cause),
    });
  }

  if (response.status === 204) return undefined as T;

  const body = await parseBody(response);

  if (!response.ok) {
    if (response.status === 401) {
      window.dispatchEvent(new CustomEvent(UNAUTHENTICATED_EVENT));
    }
    if (isApiErrorBody(body)) {
      throw new ApiRequestError(
        body.error.code,
        body.error.message,
        response.status,
        body.error.details,
      );
    }
    throw new ApiRequestError('INTERNAL', zh.app.unknownError, response.status);
  }

  return body as T;
}

/** Streams a file through the browser's own download path (never fetched into JS). */
export function downloadUrl(path: string, query: RequestOptions['query']): string {
  return buildUrl(path, query);
}
